import { useCoreService } from 'core/services/useCoreService';
import { HOME_PATH } from 'navigation/routes';
import { createContext, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDataFetcher } from 'shared/http';
import { useNotifications } from 'shared/notifications';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { CentroCostoDisponibles } from '../models/CentroCostoDisponibles/CentroCostoDisponibles';
import { SearchCostCentreTotalsError } from '../models/errors/SearchCostCentreTotalsError';
import { CentroCostoLista } from '../models/CentroCostoLista/CentroCostoLista';
import { Status } from '../models/CentroCostoLista/Status';
import { TemplateError } from '../models/errors/TemplateError';

interface IMassFundsDistributionContext {
  availableCostCentres: CentroCostoDisponibles;
  downloadTemplate: (
    parentCostCentre: CentroCostoLista,
    selectedChildren: number[],
  ) => Promise<any>;
  uploadTemplate: (file: File) => Promise<any>;
}

export const MassFundsDistributionContext = createContext<IMassFundsDistributionContext>({
  availableCostCentres: null,
  downloadTemplate: () => null,
  uploadTemplate: () => null,
});

export const MassFundsDistributionProvider = ({ children }) => {
  const [availableCostCentres, setAvailableCCsRequest] =
    useDataFetcher<CentroCostoDisponibles>();
  const coreService = useCoreService();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  // TODO Cambiar esto totalmente;
  useEffect(() => {
    const mapChildren = (list: any[], elements: any[]) => {
      if (elements != null && elements.length > 0) {
        elements.forEach((cc: any) => {
          list.push(cc);
          if (cc.hijos != null && cc.hijos.length > 0) {
            mapChildren(list, cc.hijos);
          }
        });
      }
    };

    setAvailableCCsRequest(() =>
      coreService
        .getCostCentreListMassDistribution()
        .then((response) => {
          if (response.body == null || response.body.length <= 0) {
            return null;
          }
          const list = [];
          mapChildren(list, response.body);
          return new CentroCostoDisponibles(list);
        })
        .catch((err) => {
          const message = new SearchCostCentreTotalsError(err.error).message;
          addNotification(message, 'error');
          navigate(HOME_PATH);
          return null;
        }),
    );
    // eslint-disable-next-line
  }, [setAvailableCCsRequest, navigate, coreService]);

  const value = {
    availableCostCentres: availableCostCentres?.data || null,
    downloadTemplate: (centroCostoPadre: CentroCostoLista, centroCostoIds: number[]) =>
      coreService
        .getMassDistributionTemplate({ padreId: centroCostoPadre.id, centroCostoIds })
        .then((data) => {
          addNotification('Su descarga comenzará en breve', 'info');
          return data.body;
        })
        .then((res) => {
          const url = URL.createObjectURL(res.file);
          return {
            url,
            name: res.name,
          };
        })
        .catch((err) => {
          const message = new TemplateError(err.error).message;
          addNotification(message, 'error');
          return null;
        }),
    uploadTemplate: (file: File) =>
      coreService
        .validateMassDistributionTemplate({ file })
        .then((data) => {
          if (data.body.status === Status.OK) {
            addNotification('Archivo subido exitosamente', 'success');
            return data.body;
          } else {
            const type = data.body.status === Status.FAILED ? 'info' : 'error';
            addNotification(data.body.reason, type);
          }
          return null;
        })
        .catch(() => {
          addNotification('Ocurrió un error desconocido', 'error');
          return null;
        }),
  };

  return (
    <MassFundsDistributionContext.Provider value={value}>
      <LoadingSpinner isLoading={availableCostCentres.isLoading}>
        {availableCostCentres.data && children}
      </LoadingSpinner>
    </MassFundsDistributionContext.Provider>
  );
};
